// ═══════════════════════════════════════════════════════════
// Public Portal — Track Request Status
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IconSearch, IconCheck, IconClock, IconMail,
  IconFileText, IconAlertTriangle, IconLoader2,
  IconDownload, IconSend, IconUpload, IconX, IconCreditCard, IconMessageCircle,
} from '@tabler/icons-react';
import { usePublicAuthStore } from '../../store/publicAuthStore';
import { getMyRequests } from '../../api/publicAuth';
import { FIELDS } from '../../api/fields';
import { CLOCK_COLORS, formatDaysRemaining } from '../../lib/deadlineClock';
import { formatDate } from '../../lib/format';
import { trackRequestPublic, updateRequest, type PublicTrackResult } from '../../api/requests';
import { getReleasedDocuments, getDownloadUrlForRelease, type FoiaDocument } from '../../api/foiaDocuments';
import { listInvoicesForRequest, requestFeeWaiver, type Invoice } from '../../api/invoices';
import { getPaymentTokenForInvoice, buildPaymentUrl } from '../../api/paymentTokens';
import { formatMoney } from '../../lib/format';
import { listCorrespondenceForRequest, sendCorrespondence, type CorrespondenceRecord } from '../../api/correspondence';
import { uploadAndCreateDocument } from '../../api/foiaDocuments';
import { useJurisdiction } from '../../contexts/JurisdictionContext';

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const ACTION_REQUEST_PREFIX = '[Action Required] ';

// ── Phase stepper ───────────────────────────────────────────────────────────

const PORTAL_PHASES = [
  { number: 1, label: 'Received',        statuses: ['submitted', 'acknowledged'] },
  { number: 2, label: 'Under Review',    statuses: ['inProgress'] },
  { number: 3, label: 'Collecting',      statuses: ['awaitingRequester', 'awaitingPayment', 'onHold'] },
  { number: 4, label: 'Pending Release', statuses: ['pendingReview'] },
  { number: 5, label: 'Closed',          statuses: ['completed', 'denied', 'withdrawn'] },
];

function getPortalPhase(status: string): number {
  return PORTAL_PHASES.find(p => p.statuses.includes(status))?.number ?? 1;
}

function buildMessage(status: string): string {
  switch (status) {
    case 'submitted':         return 'Your request has been received and is pending acknowledgment.';
    case 'acknowledged':      return 'Your request has been acknowledged and is being processed.';
    case 'inProgress':        return 'Your request is being processed and records are being gathered.';
    case 'awaitingRequester': return 'Action required: we need clarification before we can proceed. Please check your email and reply.';
    case 'awaitingPayment':   return 'Your request is on hold pending fee payment. Please check your email for an invoice.';
    case 'pendingReview':     return 'Your request is under review. A response will be issued soon.';
    case 'completed':         return 'Your request is complete. Any released records are available for download below.';
    case 'denied':            return 'Your request has been denied. You should have received a denial letter with the applicable exemptions.';
    case 'withdrawn':         return 'This request has been withdrawn.';
    case 'onHold':            return 'Your request is temporarily on hold.';
    default:                  return 'Your request is being processed.';
  }
}

// ── Status display ─────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  submitted:         'Submitted',
  acknowledged:      'Acknowledged',
  inProgress:        'In Progress',
  awaitingRequester: 'Action Required',
  awaitingPayment:   'Payment Required',
  pendingReview:     'Under Review',
  completed:         'Completed',
  denied:            'Denied',
  withdrawn:         'Withdrawn',
  onHold:            'On Hold',
};

const STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  submitted:         { bg: 'var(--cds-cerulean-50)', text: 'var(--cds-blue-700)' },
  acknowledged:      { bg: 'var(--cds-green-50)', text: 'var(--cds-green-700)' },
  inProgress:        { bg: 'var(--color-primary-50, #F7F8FD)', text: 'var(--color-primary-700)' },
  awaitingRequester: { bg: 'var(--cds-orange-50)', text: 'var(--cds-terracotta-700)' },
  awaitingPayment:   { bg: 'var(--cds-orange-50)', text: 'var(--cds-terracotta-700)' },
  pendingReview:     { bg: 'var(--cds-violet-50)', text: 'var(--cds-violet-600)' },
  completed:         { bg: 'var(--cds-green-50)', text: '#166534' },
  denied:            { bg: 'var(--cds-red-50)', text: 'var(--cds-red-700)' },
  withdrawn:         { bg: 'var(--color-neutral-100)', text: 'var(--color-neutral-500)' },
  onHold:            { bg: 'var(--color-neutral-100)', text: 'var(--color-neutral-600)' },
};


// ── My Requests list with filters ────────────────────────────────────────

const RF = FIELDS.requests;

const MY_STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted', acknowledged: 'Acknowledged', inProgress: 'In Progress',
  awaitingRequester: 'Response Needed', awaitingPayment: 'Payment Required',
  pendingReview: 'Under Review', completed: 'Completed', denied: 'Denied',
  withdrawn: 'Withdrawn', onHold: 'On Hold',
};

const MY_STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  submitted:         { bg: 'var(--cds-cerulean-50)', text: 'var(--cds-blue-700)' },
  acknowledged:      { bg: 'var(--cds-green-50)', text: 'var(--cds-green-700)' },
  inProgress:        { bg: 'var(--cds-blurple-50)', text: 'var(--cds-blurple-700)' },
  awaitingRequester: { bg: 'var(--cds-orange-50)', text: 'var(--cds-terracotta-700)' },
  awaitingPayment:   { bg: 'var(--cds-orange-50)', text: 'var(--cds-terracotta-700)' },
  pendingReview:     { bg: 'var(--cds-violet-50)', text: 'var(--cds-violet-600)' },
  completed:         { bg: 'var(--cds-green-50)', text: '#166534' },
  denied:            { bg: 'var(--cds-red-50)', text: 'var(--cds-red-700)' },
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'inProgress', label: 'In Progress' },
  { value: 'awaitingRequester', label: 'Response Needed' },
  { value: 'awaitingPayment', label: 'Payment Required' },
  { value: 'pendingReview', label: 'Under Review' },
  { value: 'completed', label: 'Completed' },
  { value: 'denied', label: 'Denied' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

function MyRequestsList({
  myRequests,
  myReqLoading,
  onSelect,
}: {
  myRequests: Record<string, unknown>[] | null;
  myReqLoading: boolean;
  onSelect: (num: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [keyword,      setKeyword]      = useState('');

  const filtered = (myRequests ?? []).filter(r => {
    if (statusFilter && String(r[RF.status] ?? '') !== statusFilter) return false;
    const submitted = String(r[RF.dateSubmitted] ?? '').slice(0, 10);
    if (dateFrom && submitted < dateFrom) return false;
    if (dateTo   && submitted > dateTo)   return false;
    if (keyword) {
      const kw = keyword.toLowerCase();
      const subject = String(r[RF.subject] ?? '').toLowerCase();
      const num     = String(r[RF.requestNumber] ?? '').toLowerCase();
      if (!subject.includes(kw) && !num.includes(kw)) return false;
    }
    return true;
  });

  const hasFilters = statusFilter || dateFrom || dateTo || keyword;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-neutral-800)' }}>
          Your Requests
        </div>
        {myRequests && myRequests.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>
            {hasFilters ? `${filtered.length} of ${myRequests.length}` : myRequests.length} request{myRequests.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Filters */}
      {!myReqLoading && myRequests && myRequests.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16,
          padding: '12px 14px', background: 'var(--color-neutral-50)',
          border: '1px solid var(--color-neutral-200)', borderRadius: 8,
        }}>
          {/* Keyword */}
          <input
            type="text"
            className="form-input"
            placeholder="Search subject or request #"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            style={{ flex: '1 1 180px', fontSize: 13, padding: '5px 10px', minWidth: 140 }}
          />

          {/* Status */}
          <select
            className="form-input"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ flex: '1 1 150px', fontSize: 13, padding: '5px 10px', minWidth: 140 }}
          >
            {STATUS_FILTER_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Date range */}
          <div style={{ flex: '1 1 300px', minWidth: 260, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Submitted
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                style={{ flex: 1, fontSize: 13, padding: '5px 10px' }}
              />
              <span style={{ fontSize: 12, color: 'var(--color-neutral-400)', flexShrink: 0 }}>to</span>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                style={{ flex: 1, fontSize: 13, padding: '5px 10px' }}
              />
            </div>
          </div>

          {hasFilters && (
            <button
              onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); setKeyword(''); }}
              style={{
                fontSize: 12, color: 'var(--color-neutral-500)', background: 'none',
                border: '1px solid var(--color-neutral-200)', borderRadius: 4,
                padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {myReqLoading || myRequests === null ? (
        <div style={{ textAlign: 'center', padding: '32px', color: 'var(--color-neutral-400)' }}>
          <IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : myRequests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 24px', color: 'var(--color-neutral-400)', fontSize: 13 }}>
          No requests found. <a href="/portal/submit" style={{ color: 'var(--color-primary-700)' }}>Submit your first request.</a>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-neutral-400)', fontSize: 13 }}>
          No requests match your filters.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 520, overflowY: 'auto', paddingRight: 2 }}>
          {filtered.map(r => {
            const status = String(r[RF.status] ?? '');
            const num    = String(r[RF.requestNumber] ?? '');
            const ss = MY_STATUS_STYLE[status] ?? { bg: 'var(--cds-gray-50)', text: 'var(--color-secondary-700)' };
            const needsAction = status === 'awaitingPayment' || status === 'awaitingRequester';
            return (
              <div
                key={r['record_id'] as number}
                onClick={() => onSelect(num)}
                style={{
                  padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                  border: `1px solid ${needsAction ? 'var(--cds-yellow-300)' : 'var(--color-neutral-200)'}`,
                  background: needsAction ? 'var(--cds-yellow-50)' : 'white',
                  transition: 'box-shadow 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--color-primary-700)' }}>{num}</span>
                  <span style={{ padding: '1px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: ss.bg, color: ss.text }}>
                    {MY_STATUS_LABELS[status] ?? status}
                  </span>
                  {needsAction && (
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--cds-yellow-600)', fontWeight: 600 }}>
                      {status === 'awaitingPayment'
                        ? <><IconCreditCard size={11} /> Payment due</>
                        : <><IconMessageCircle size={11} /> Response needed</>
                      }
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {String(r[RF.subject] ?? '')}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-neutral-400)', marginTop: 3 }}>
                  Submitted {r[RF.dateSubmitted] ? String(r[RF.dateSubmitted]).slice(0, 10) : '—'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Track() {
  const { requestNumber: urlNumber } = useParams<{ requestNumber?: string }>();
  const navigate = useNavigate();
  const jurisdiction = useJurisdiction();
  const publicUser = usePublicAuthStore(s => s.user);

  const [searchInput, setSearchInput] = useState(urlNumber ?? '');
  const [searched, setSearched] = useState(urlNumber ?? '');
  const [result, setResult] = useState<PublicTrackResult | 'not-found' | null>(null);
  const [loading, setLoading] = useState(false);
  const [releasedDocs, setReleasedDocs] = useState<FoiaDocument[]>([]);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [actionRequest, setActionRequest] = useState<CorrespondenceRecord | null>(null);
  const [correspondence, setCorrespondence] = useState<CorrespondenceRecord[]>([]);
  const [reply, setReply] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [replySending, setReplySending] = useState(false);
  const [replySent, setReplySent] = useState(false);
  const [replyDragOver, setReplyDragOver] = useState(false);
  const [myRequests, setMyRequests]   = useState<Record<string, unknown>[] | null>(null);
  const [myReqLoading, setMyReqLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [paymentUrls, setPaymentUrls] = useState<Record<number, string>>({});
  // Per-invoice waiver state keyed by invoiceId
  const [waiverReasons,  setWaiverReasons]  = useState<Record<number, string>>({});
  const [waiverSending,  setWaiverSending]  = useState<Record<number, boolean>>({});
  const [waiverSent,     setWaiverSent]     = useState<Record<number, boolean>>({});
  const [msgReply,        setMsgReply]       = useState('');
  const [msgReplySending, setMsgReplySending] = useState(false);
  const [msgReplySent,    setMsgReplySent]   = useState(false);

  async function lookup(num: string) {
    if (!num.trim()) return;
    setLoading(true);
    setSearched(num.trim());
    setReleasedDocs([]);
    setActionRequest(null);
    setReplySent(false);
    setReply('');
    setReplyFiles([]);
    setInvoices([]);
    setPaymentUrls({});
    setWaiverReasons({});
    setWaiverSending({});
    setWaiverSent({});
    setMsgReply('');
    setMsgReplySent(false);
    try {
      const found = await trackRequestPublic(num.trim().toUpperCase());
      setResult(found ?? 'not-found');
      if (found) {
        getReleasedDocuments(found.recordId, { anonymous: true }).then(setReleasedDocs).catch(() => {});
        // Fetch all invoices; surface payment notices for any sent/estimateOnly ones.
        listInvoicesForRequest(found.recordId, { anonymous: true })
          .then(async invs => {
            setInvoices(invs);
            const urls: Record<number, string> = {};
            await Promise.all(
              invs
                .filter(i => i.status === 'sent' || i.status === 'estimateOnly')
                .map(async i => {
                  const t = await getPaymentTokenForInvoice(found.recordId, i.recordId, { anonymous: true }).catch(() => null);
                  if (t) urls[i.recordId] = buildPaymentUrl(t.token);
                })
            );
            setPaymentUrls(urls);
          })
          .catch(() => {});
        // Always fetch correspondence so the customer can see messages
        listCorrespondenceForRequest(found.recordId, { anonymous: true })
          .then(records => {
            // Show non-internal correspondence to the customer
            const visible = records.filter(r => r.direction !== 'internal');
            setCorrespondence(visible);
            const pending = records.find(r =>
              r.direction === 'outbound' && r.subject?.startsWith(ACTION_REQUEST_PREFIX)
            ) ?? null;
            setActionRequest(pending);

            // Self-heal: if status is awaitingRequester but customer already replied,
            // fix the status so the Action Required card doesn't keep showing
            if (found.status === 'awaitingRequester' && visible.some(r => r.direction === 'inbound')) {
              updateRequest(found.recordId, { status: 'inProgress' }).then(() => {
                setResult(prev =>
                  prev && prev !== 'not-found'
                    ? { ...prev, status: 'inProgress' }
                    : prev
                );
              }).catch(() => {});
            }
          })
          .catch(() => {});
      }
    } catch {
      setResult('not-found');
    } finally {
      setLoading(false);
    }
  }

  async function handleReply() {
    if ((!reply.trim() && replyFiles.length === 0) || !result || result === 'not-found') return;
    setReplySending(true);
    try {
      // Upload any attached files first
      for (const file of replyFiles) {
        await uploadAndCreateDocument({
          requestId:       result.recordId,
          file,
          source:          'requester',
          uploadedByEmail: '',
          notes:           'Submitted by requester via portal in response to action request',
          anonymous:       true,
        });
      }

      // Build body — always append a file note when documents were attached
      let body = reply.trim();
      if (replyFiles.length > 0) {
        const fileNames = replyFiles.map(f => f.name).join(', ');
        const fileNote = `📎 ${replyFiles.length} document${replyFiles.length !== 1 ? 's' : ''} submitted: ${fileNames}`;
        body = body ? `${body}\n\n${fileNote}` : fileNote;
      }
      await sendCorrespondence({
        requestId:   result.recordId,
        direction:   'inbound',
        subject:     actionRequest
          ? `Re: ${actionRequest.subject.slice(ACTION_REQUEST_PREFIX.length)}`
          : 'Requester Response',
        body,
        isAutomated: false,
        anonymous:   true,
      });

      // Transition status back to inProgress so the Action Required card
      // doesn't reappear on reload
      try {
        await updateRequest(result.recordId, { status: 'inProgress' });
        setResult(prev =>
          prev && prev !== 'not-found'
            ? { ...prev, status: 'inProgress', isPaused: false }
            : prev
        );
      } catch { /* don't fail the reply if status update fails */ }

      setReplySent(true);
      setReply('');
      setReplyFiles([]);
      // Refresh correspondence immediately so the sent message appears in the Messages panel
      listCorrespondenceForRequest(result.recordId, { anonymous: true })
        .then(records => setCorrespondence(records.filter(r => r.direction !== 'internal')))
        .catch(() => {});
    } finally {
      setReplySending(false);
    }
  }

  async function handleMsgReply() {
    if (!msgReply.trim() || !result || result === 'not-found') return;
    setMsgReplySending(true);
    try {
      const lastOutbound = correspondence.find(c => c.direction === 'outbound'); // array is desc, so first outbound is newest
      const subject = lastOutbound?.subject
        ? `Re: ${lastOutbound.subject.replace(ACTION_REQUEST_PREFIX, '')}`
        : 'Requester Message';
      await sendCorrespondence({
        requestId:   result.recordId,
        direction:   'inbound',
        subject,
        body:        msgReply.trim(),
        isAutomated: false,
        anonymous:   true,
      });
      setMsgReplySent(true);
      setMsgReply('');
      listCorrespondenceForRequest(result.recordId, { anonymous: true })
        .then(records => setCorrespondence(records.filter(r => r.direction !== 'internal')))
        .catch(() => {});
    } finally {
      setMsgReplySending(false);
    }
  }

  async function handleFeeWaiverRequest(invoice: Invoice) {
    if (!result || result === 'not-found') return;
    const id = invoice.recordId;
    setWaiverSending(prev => ({ ...prev, [id]: true }));
    try {
      await requestFeeWaiver(id, waiverReasons[id] ?? '', invoice.notes, { anonymous: true });
      setWaiverSent(prev => ({ ...prev, [id]: true }));
      setInvoices(prev => prev.map(i => i.recordId === id ? { ...i, feeWaiverStatus: 'pending' } : i));
    } finally {
      setWaiverSending(prev => ({ ...prev, [id]: false }));
    }
  }

  async function handleDownload(doc: FoiaDocument) {
    setDownloadingId(doc.recordId);
    try {
      const url = await getDownloadUrlForRelease(doc, { anonymous: true });
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.fileName;
        a.click();
      }
    } finally {
      setDownloadingId(null);
    }
  }

  useEffect(() => {
    if (urlNumber) {
      setSearchInput(urlNumber);
      lookup(urlNumber);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlNumber]);

  // Refs so the silent refresh callback can read current values without stale closures
  const searchedRef = useRef(searched);
  useEffect(() => { searchedRef.current = searched; }, [searched]);
  const resultRef = useRef(result);
  useEffect(() => { resultRef.current = result; }, [result]);
  const publicUserRef = useRef(publicUser);
  useEffect(() => { publicUserRef.current = publicUser; }, [publicUser]);

  // Background refresh — does NOT show the loading spinner or clear existing content.
  // Called whenever a foia:datachange event fires on the public portal.
  const silentRefresh = useCallback(async () => {
    const current = resultRef.current;
    if (current && current !== 'not-found') {
      // Re-fetch request status + invoices without touching loading state
      const found = await trackRequestPublic(current.requestNumber).catch(() => null);
      if (found) {
        setResult(found);
        const invs = await listInvoicesForRequest(found.recordId, { anonymous: true }).catch(() => null);
        if (invs) {
          setInvoices(invs);
          const urls: Record<number, string> = {};
          await Promise.all(
            invs
              .filter(i => i.status === 'sent' || i.status === 'estimateOnly')
              .map(async i => {
                const t = await getPaymentTokenForInvoice(found.recordId, i.recordId, { anonymous: true }).catch(() => null);
                if (t) urls[i.recordId] = buildPaymentUrl(t.token);
              })
          );
          setPaymentUrls(urls);
        }
        getReleasedDocuments(found.recordId, { anonymous: true }).then(setReleasedDocs).catch(() => {});
      }
    } else if (searchedRef.current) {
      // No result loaded yet but user searched — do a full lookup
      lookup(searchedRef.current);
    }
    // Also refresh the "My Requests" list for logged-in users
    const pu = publicUserRef.current;
    if (pu?.email) {
      getMyRequests(pu.email).then(setMyRequests).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const PORTAL_TABLES = new Set(['pa1l6s8qe', 'fz6kyugqq', 'z51jczl9a', 'i1sy9y14o']);
    function handleDataChange(e: Event) {
      const tables: string[] = (e as CustomEvent).detail?.tables ?? [];
      if (tables.some(t => PORTAL_TABLES.has(t))) silentRefresh();
    }
    window.addEventListener('foia:datachange', handleDataChange);
    return () => window.removeEventListener('foia:datachange', handleDataChange);
  }, [silentRefresh]);

  // Load the customer's own requests when logged in and no specific request is being looked up
  useEffect(() => {
    if (!publicUser?.email || urlNumber) return;
    setMyReqLoading(true);
    getMyRequests(publicUser.email)
      .then(setMyRequests)
      .catch(() => setMyRequests([]))
      .finally(() => setMyReqLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicUser?.email]);

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = searchInput.trim();
    if (!trimmed) return;
    navigate(`/portal/track/${encodeURIComponent(trimmed)}`, { replace: true });
    lookup(trimmed);
  }

  const isClosed = result && result !== 'not-found'
    ? ['completed', 'denied', 'withdrawn'].includes(result.status)
    : false;


  // Build a clock-colors-compatible object from pre-computed fields
  const clockResult = result && result !== 'not-found' ? {
    daysRemaining: result.daysRemaining ?? 0,
    isPaused: result.isPaused ?? false,
    isOverdue: result.isOverdue ?? false,
    effectiveDeadline: result.effectiveDeadline ? new Date(result.effectiveDeadline) : new Date(),
    status: result.isOverdue ? 'overdue' : (result.daysRemaining ?? 99) <= 3 ? 'critical' : (result.daysRemaining ?? 99) <= 7 ? 'warning' : 'ok',
  } : null;

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 4px' }}>Track Your Request</h1>
      <p style={{ fontSize: 13, color: 'var(--color-neutral-500)', margin: '0 0 24px' }}>
        Enter your request number to check the status of your public records request.
      </p>

      {/* Search form */}
      <form onSubmit={onSearch} style={{ display: 'flex', gap: 0, marginBottom: 28 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <IconSearch size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-400)' }} />
          <input
            type="text"
            className="form-input"
            style={{ paddingLeft: 36, borderRadius: '6px 0 0 6px', fontSize: 14 }}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="e.g. 2026-0001"
            required
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          style={{ borderRadius: '0 6px 6px 0', padding: '0 20px', fontSize: 13 }}
        >
          Look Up
        </button>
      </form>

      {/* Result */}
      {!loading && result === 'not-found' && (
        <div style={{
          textAlign: 'center', padding: '40px 24px', background: 'var(--cds-red-50)',
          borderRadius: 8, border: '1px solid var(--cds-red-200)',
        }}>
          <IconAlertTriangle size={28} style={{ color: '#DC2626', marginBottom: 10 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--cds-red-800)', marginBottom: 6 }}>Request Not Found</div>
          <div style={{ fontSize: 13, color: 'var(--cds-red-700)' }}>
            No request found for <strong>{searched}</strong>. Check the number and try again, or contact us at{' '}
            <a href={`mailto:${jurisdiction?.contactInfo?.match(/[\w.-]+@[\w.-]+/)?.[0] ?? 'records@example.gov'}`} style={{ color: 'var(--cds-red-700)' }}>
              {jurisdiction?.contactInfo?.match(/[\w.-]+@[\w.-]+/)?.[0] ?? 'records@example.gov'}
            </a>.
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--color-neutral-400)' }}>
          <IconLoader2 size={28} style={{ marginBottom: 10, opacity: 0.6, animation: 'spin 1s linear infinite' }} />
          <div style={{ fontSize: 14 }}>Looking up your request…</div>
        </div>
      )}

      {!loading && result && result !== 'not-found' && clockResult && (() => {
        const currentPhase = getPortalPhase(result.status);
        const actionCards = invoices.filter(inv => inv.status === 'sent' || inv.status === 'estimateOnly');
        return (
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>

            {/* ── LEFT COLUMN ── */}
            <div style={{ flex: '1 1 380px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Header card */}
              <div className="card" style={{ padding: 0 }}>
                <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--color-neutral-100)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: 'var(--color-primary-700)' }}>
                      {result.requestNumber}
                    </span>
                    <span style={{
                      padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                      ...(STATUS_STYLE[result.status] ?? { bg: 'var(--color-neutral-100)', text: 'var(--color-neutral-500)' }),
                    }}>
                      {STATUS_LABELS[result.status] ?? result.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-neutral-900)', marginBottom: 4 }}>{result.subject}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>
                    Submitted {formatDate(result.dateSubmitted)}
                  </div>
                </div>

                {/* Status message */}
                <div style={{ padding: '12px 18px', background: result.status === 'awaitingRequester' ? 'var(--cds-orange-50)' : 'var(--color-neutral-50)', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  {result.status === 'awaitingRequester'
                    ? <IconAlertTriangle size={16} style={{ color: 'var(--cds-yellow-600)', marginTop: 1, flexShrink: 0 }} />
                    : <IconMail size={16} style={{ color: 'var(--color-neutral-400)', marginTop: 1, flexShrink: 0 }} />
                  }
                  <div style={{ fontSize: 13, color: result.status === 'awaitingRequester' ? 'var(--cds-yellow-800)' : 'var(--color-neutral-600)', lineHeight: 1.6 }}>
                    {buildMessage(result.status)}
                  </div>
                </div>

                {/* Deadline clock */}
                {!isClosed && (
                  <div style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                      borderRadius: 99, fontSize: 13, fontWeight: 700,
                      background: CLOCK_COLORS[clockResult.status as keyof typeof CLOCK_COLORS]?.bg ?? 'var(--color-neutral-100)',
                      color: CLOCK_COLORS[clockResult.status as keyof typeof CLOCK_COLORS]?.text ?? 'var(--color-neutral-500)',
                      border: `1px solid ${CLOCK_COLORS[clockResult.status as keyof typeof CLOCK_COLORS]?.border ?? 'var(--color-neutral-200)'}`,
                    }}>
                      <IconClock size={14} />
                      {formatDaysRemaining(clockResult.daysRemaining, clockResult.isPaused, false)}
                    </div>
                    {result.effectiveDeadline && (
                      <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>
                        Response due {formatDate(result.effectiveDeadline)}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Phase stepper */}
              <div className="card" style={{ padding: '14px 18px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-400)', marginBottom: 14 }}>
                  Where is my request?
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  {PORTAL_PHASES.map((phase, i) => {
                    const isComplete = currentPhase > phase.number;
                    const isCurrent  = currentPhase === phase.number;
                    return (
                      <div key={phase.number} style={{ display: 'flex', alignItems: 'flex-start', flex: i < PORTAL_PHASES.length - 1 ? 1 : 0 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          <div style={{
                            width: 26, height: 26, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700,
                            background: (isComplete || isCurrent) ? 'var(--color-primary-700)' : 'var(--color-neutral-100)',
                            color: (isComplete || isCurrent) ? 'white' : 'var(--color-neutral-400)',
                            border: `2px solid ${(isComplete || isCurrent) ? 'var(--color-primary-700)' : 'var(--color-neutral-200)'}`,
                            outline: isCurrent ? '3px solid var(--color-primary-100, #E0E7FF)' : 'none',
                          }}>
                            {isComplete ? <IconCheck size={11} /> : phase.number}
                          </div>
                          <span style={{
                            fontSize: 11, fontWeight: isCurrent ? 700 : 400, whiteSpace: 'nowrap', textAlign: 'center',
                            color: isCurrent ? 'var(--color-primary-700)' : isComplete ? 'var(--color-neutral-600)' : 'var(--color-neutral-400)',
                          }}>
                            {phase.label}
                          </span>
                        </div>
                        {i < PORTAL_PHASES.length - 1 && (
                          <div style={{
                            flex: 1, height: 2, marginTop: 12,
                            background: isComplete ? 'var(--color-primary-300, #A5B4FC)' : 'var(--color-neutral-200)',
                          }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>{/* end left column */}

            {/* ── RIGHT COLUMN ── */}
            <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Action Required card */}
              {actionRequest && (() => {
                const customerReplies = correspondence.filter(c => c.direction === 'inbound');
                const hasReplied = customerReplies.length > 0;
                return (
                  <div style={{
                    border: `1px solid ${hasReplied ? 'var(--cds-green-200)' : 'var(--cds-yellow-200)'}`, borderRadius: 10, overflow: 'hidden',
                    background: hasReplied ? 'var(--cds-green-50)' : 'var(--cds-yellow-50)',
                  }}>
                    <div style={{ padding: '12px 18px', borderBottom: `1px solid ${hasReplied ? 'var(--cds-green-200)' : 'var(--cds-yellow-200)'}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {hasReplied
                        ? <IconCheck size={15} style={{ color: 'var(--cds-green-700)' }} />
                        : <IconAlertTriangle size={15} style={{ color: 'var(--cds-yellow-600)' }} />
                      }
                      <span style={{ fontSize: 13, fontWeight: 700, color: hasReplied ? '#166534' : 'var(--cds-yellow-800)' }}>
                        {hasReplied ? 'Response Submitted' : 'Action Required — Your Response is Needed'}
                      </span>
                    </div>
                    <div style={{ padding: '12px 18px', borderBottom: `1px solid ${hasReplied ? 'var(--cds-green-200)' : 'var(--cds-yellow-200)'}` }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: hasReplied ? '#166534' : 'var(--cds-yellow-800)', marginBottom: 6 }}>
                        {actionRequest.subject.slice(ACTION_REQUEST_PREFIX.length)}
                      </div>
                      <div style={{ fontSize: 13, color: hasReplied ? 'var(--cds-green-700)' : '#78350F', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                        {actionRequest.body}
                      </div>
                      <div style={{ fontSize: 11, color: hasReplied ? '#166534' : 'var(--cds-yellow-700)', marginTop: 8, opacity: 0.7 }}>
                        Sent {formatDate(actionRequest.sentAt)}
                      </div>
                    </div>
                    {hasReplied ? (
                      <div>
                        {customerReplies.map(r => (
                          <div key={r.recordId} style={{ padding: '12px 18px', borderBottom: '1px solid var(--cds-green-200)', background: 'white' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-600)' }}>Your Response</span>
                              <span style={{ fontSize: 11, color: 'var(--color-neutral-400)' }}>{r.sentAt ? formatDate(r.sentAt) : ''}</span>
                            </div>
                            <div style={{ fontSize: 13, color: 'var(--color-neutral-700)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{r.body}</div>
                          </div>
                        ))}
                        <div style={{ padding: '10px 18px', fontSize: 13, color: '#166534', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <IconCheck size={14} /> The records office has been notified and will follow up with you.
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: '14px 18px' }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--cds-yellow-800)', display: 'block', marginBottom: 6 }}>Your Response</label>
                        <textarea
                          className="form-input"
                          rows={3}
                          style={{ fontFamily: 'inherit', resize: 'vertical', fontSize: 13, lineHeight: 1.6, marginBottom: 10, width: '100%', boxSizing: 'border-box' }}
                          placeholder="Type your response here… (optional if attaching documents)"
                          value={reply}
                          onChange={e => setReply(e.target.value)}
                        />
                        <div
                          onDragOver={e => { e.preventDefault(); setReplyDragOver(true); }}
                          onDragLeave={() => setReplyDragOver(false)}
                          onDrop={e => {
                            e.preventDefault(); setReplyDragOver(false);
                            const incoming = Array.from(e.dataTransfer.files);
                            setReplyFiles(prev => { const existing = new Set(prev.map(f => f.name + f.size)); return [...prev, ...incoming.filter(f => !existing.has(f.name + f.size))]; });
                          }}
                          onClick={() => document.getElementById('reply-file-input')?.click()}
                          style={{ border: `2px dashed ${replyDragOver ? 'var(--cds-yellow-600)' : 'var(--cds-yellow-200)'}`, borderRadius: 8, padding: '12px 14px', textAlign: 'center', cursor: 'pointer', background: replyDragOver ? 'var(--cds-yellow-50)' : 'transparent', transition: 'all 0.15s', marginBottom: 10 }}
                        >
                          <IconUpload size={15} style={{ color: 'var(--cds-yellow-600)', marginBottom: 3 }} />
                          <div style={{ fontSize: 12, color: 'var(--cds-yellow-800)' }}>Drop files or <span style={{ fontWeight: 600 }}>click to browse</span></div>
                        </div>
                        <input id="reply-file-input" type="file" multiple style={{ display: 'none' }}
                          onChange={e => {
                            if (!e.target.files) return;
                            const incoming = Array.from(e.target.files);
                            setReplyFiles(prev => { const existing = new Set(prev.map(f => f.name + f.size)); return [...prev, ...incoming.filter(f => !existing.has(f.name + f.size))]; });
                            e.target.value = '';
                          }}
                        />
                        {replyFiles.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                            {replyFiles.map((f, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 6, background: '#FEF9C3', border: '1px solid var(--cds-yellow-200)' }}>
                                <IconFileText size={12} style={{ color: 'var(--cds-yellow-600)', flexShrink: 0 }} />
                                <span style={{ flex: 1, fontSize: 12, color: '#78350F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                                <span style={{ fontSize: 11, color: 'var(--cds-yellow-700)', flexShrink: 0 }}>{formatBytes(f.size)}</span>
                                <button type="button" onClick={e => { e.stopPropagation(); setReplyFiles(prev => prev.filter((_, idx) => idx !== i)); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cds-yellow-800)', padding: 2, flexShrink: 0 }}><IconX size={12} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button className="btn btn-primary btn-sm" style={{ gap: 5, background: 'var(--cds-yellow-600)', borderColor: 'var(--cds-yellow-600)' }}
                            disabled={(!reply.trim() && replyFiles.length === 0) || replySending} onClick={handleReply}>
                            {replySending ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconSend size={12} /> Send Response</>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Fee payment / waiver cards */}
              {actionCards.map(invoice => {
                const paymentUrl = paymentUrls[invoice.recordId] ?? null;
                const isSent     = waiverSent[invoice.recordId] ?? false;
                const isSending  = waiverSending[invoice.recordId] ?? false;
                const reason     = waiverReasons[invoice.recordId] ?? '';
                return (
                  <div key={invoice.recordId} style={{ border: '1px solid var(--cds-yellow-200)', borderRadius: 10, overflow: 'hidden', background: 'var(--cds-yellow-50)' }}>
                    <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--cds-yellow-200)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <IconAlertTriangle size={15} style={{ color: 'var(--cds-yellow-600)' }} />
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--cds-yellow-800)' }}>
                          {invoice.status === 'estimateOnly' ? 'Fee Estimate' : 'Fee Payment Required'}
                        </span>
                        {actionCards.length > 1 && <span style={{ fontSize: 11, color: 'var(--cds-yellow-700)', marginLeft: 8 }}>Invoice #{invoice.recordId}</span>}
                      </div>
                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--cds-terracotta-800)' }}>{formatMoney(invoice.total)}</span>
                        {paymentUrl && invoice.feeWaiverStatus !== 'approved' && invoice.status === 'sent' && (
                          <a href={paymentUrl} className="btn btn-primary btn-sm" style={{ gap: 5, background: 'var(--cds-yellow-600)', borderColor: 'var(--cds-yellow-600)', textDecoration: 'none' }}>Pay Online →</a>
                        )}
                      </div>
                    </div>
                    {invoice.items.length > 0 && (
                      <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--cds-yellow-200)' }}>
                        {invoice.items.map(item => (
                          <div key={item.recordId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--cds-yellow-800)', padding: '3px 0' }}>
                            <span>{item.description}</span><span style={{ fontWeight: 600 }}>{formatMoney(item.total)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {invoice.feeWaiverStatus === 'approved' ? (
                      <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--cds-green-700)', background: 'var(--cds-green-50)' }}>
                        <IconCheck size={14} /> Your fee waiver has been approved. No payment is required.
                      </div>
                    ) : invoice.feeWaiverStatus === 'denied' ? (
                      <div style={{ padding: '14px 18px', fontSize: 13, color: 'var(--cds-red-700)' }}>Your fee waiver request was denied. Please contact the records office to arrange payment.</div>
                    ) : invoice.feeWaiverStatus === 'pending' || isSent ? (
                      <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--cds-yellow-800)' }}>
                        {isSent ? <><IconCheck size={14} style={{ color: '#166534' }} /> <span style={{ color: '#166534' }}>Waiver request submitted. We will follow up with you.</span></> : 'Your fee waiver request is under review.'}
                      </div>
                    ) : (
                      <div style={{ padding: '14px 18px' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cds-yellow-800)', marginBottom: 6 }}>Request a Fee Waiver</div>
                        <div style={{ fontSize: 12, color: 'var(--cds-yellow-700)', marginBottom: 10 }}>If you believe fees should be waived (e.g., news media, non-profit, or public interest), provide a brief explanation.</div>
                        <textarea className="form-input" rows={3} style={{ fontFamily: 'inherit', resize: 'vertical', fontSize: 13, marginBottom: 10, width: '100%', boxSizing: 'border-box' }}
                          placeholder="Explain why a fee waiver is appropriate for your request…" value={reason}
                          onChange={e => setWaiverReasons(prev => ({ ...prev, [invoice.recordId]: e.target.value }))} />
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button className="btn btn-primary btn-sm" style={{ gap: 5, background: 'var(--cds-yellow-600)', borderColor: 'var(--cds-yellow-600)' }}
                            disabled={!reason.trim() || isSending} onClick={() => handleFeeWaiverRequest(invoice)}>
                            {isSending ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconSend size={12} /> Submit Waiver Request</>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Messages */}
              <div className="card" style={{ padding: 0 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IconMail size={14} style={{ color: 'var(--color-primary-700)' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)' }}>Messages</span>
                  {correspondence.length > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>({correspondence.length})</span>
                  )}
                </div>
                {correspondence.length === 0 && (
                  <div style={{ padding: '16px', fontSize: 13, color: 'var(--color-neutral-400)', fontStyle: 'italic' }}>
                    No messages yet. Use the box below to contact the records office.
                  </div>
                )}
                {[...correspondence].reverse().map(c => {
                  const isFromStaff = c.direction === 'outbound';
                  return (
                    <div key={c.recordId} style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)', background: isFromStaff ? 'white' : 'var(--color-neutral-50)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: isFromStaff ? 'var(--color-primary-700)' : 'var(--color-neutral-600)' }}>
                          {isFromStaff ? 'Records Office' : 'You'}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>{c.sentAt ? formatDate(c.sentAt) : ''}</span>
                      </div>
                      {c.subject && (
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-neutral-800)', marginBottom: 4 }}>
                          {c.subject.replace(ACTION_REQUEST_PREFIX, '')}
                        </div>
                      )}
                      <div style={{ fontSize: 13, color: 'var(--color-neutral-700)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.body}</div>
                    </div>
                  );
                })}
                <div style={{ padding: '14px 16px', borderTop: correspondence.length > 0 ? '1px solid var(--color-neutral-100)' : 'none', background: 'var(--color-neutral-50)' }}>
                  {msgReplySent ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--cds-green-700)' }}>
                      <IconCheck size={14} /> Your message has been sent. The records office will follow up with you.
                    </div>
                  ) : (
                    <>
                      <textarea className="form-input" rows={3}
                        style={{ fontFamily: 'inherit', resize: 'vertical', fontSize: 13, lineHeight: 1.6, marginBottom: 8, width: '100%', boxSizing: 'border-box' }}
                        placeholder="Write a message to the records office…" value={msgReply} onChange={e => setMsgReply(e.target.value)} />
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-primary btn-sm" style={{ gap: 5 }}
                          disabled={!msgReply.trim() || msgReplySending} onClick={handleMsgReply}>
                          {msgReplySending ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconSend size={12} /> Send</>}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Released documents */}
              {releasedDocs.length > 0 && (
                <div className="card" style={{ padding: 0 }}>
                  <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <IconDownload size={14} style={{ color: 'var(--color-primary-700)' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)' }}>Released Documents</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-neutral-400)' }}>{releasedDocs.length} file{releasedDocs.length !== 1 ? 's' : ''}</span>
                  </div>
                  {releasedDocs.map((doc, i) => (
                    <div key={doc.recordId} style={{ padding: '11px 18px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: i < releasedDocs.length - 1 ? '1px solid var(--color-neutral-100)' : 'none' }}>
                      <IconFileText size={15} style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.fileName}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-neutral-400)', marginTop: 2 }}>
                          {doc.releasedAt ? `Released ${formatDate(doc.releasedAt)}` : 'Released'}
                          {doc.fileSize ? ` · ${(doc.fileSize / 1024).toFixed(0)} KB` : ''}
                          {doc.responsiveClass === 'partiallyResponsive' ? ' · Partially responsive (redacted)' : ''}
                        </div>
                      </div>
                      <button className="btn btn-secondary btn-sm" style={{ gap: 5, flexShrink: 0 }} disabled={downloadingId === doc.recordId} onClick={() => handleDownload(doc)}>
                        {downloadingId === doc.recordId ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <IconDownload size={12} />}
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Contact */}
              <div style={{ padding: '12px 16px', background: 'var(--color-neutral-50)', borderRadius: 8, border: '1px solid var(--color-neutral-200)', display: 'flex', gap: 10, alignItems: 'center' }}>
                <IconMail size={14} style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--color-neutral-600)' }}>
                  Questions? Email{' '}
                  <a href={`mailto:${jurisdiction?.contactInfo?.match(/[\w.-]+@[\w.-]+/)?.[0] ?? 'records@example.gov'}`} style={{ color: 'var(--color-primary-700)' }}>
                    {jurisdiction?.contactInfo?.match(/[\w.-]+@[\w.-]+/)?.[0] ?? 'records@example.gov'}
                  </a>
                  {' '}with your request number.
                </div>
              </div>

            </div>{/* end right column */}
          </div>
        );
      })()}

      {/* No search yet — show logged-in customer's requests, or generic help */}
      {!loading && result === null && (
        publicUser ? (
          <MyRequestsList
            myRequests={myRequests}
            myReqLoading={myReqLoading}
            onSelect={(num) => {
              setSearchInput(num);
              navigate(`/portal/track/${encodeURIComponent(num)}`, { replace: true });
              lookup(num);
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--color-neutral-400)' }}>
            <IconSearch size={32} style={{ marginBottom: 10, opacity: 0.5 }} />
            <div style={{ fontSize: 14 }}>Enter your request number above to see your status.</div>
            <div style={{ fontSize: 12, marginTop: 8 }}>
              Your request number was included in your confirmation email, formatted like <strong>2026-0001</strong>.
            </div>
            <div style={{ fontSize: 12, marginTop: 12 }}>
              <a href="/portal/account" style={{ color: 'var(--color-primary-700)' }}>Sign in</a> to see all your requests automatically.
            </div>
          </div>
        )
      )}
    </div>
  );
}
